import { Chess } from 'chess.js';

// --- PGN Tree Structure Definition ---
export class PgnNode {
  constructor(move = null, parent = null, moveNumber = 0) {
    // move: chess.js Move object (contains FEN before/after, SAN, etc.)
    this.move = move;
    this.parent = parent; // Reference to parent PgnNode
    this.children = []; // Array of child PgnNodes (variations)
    this.comment = '';
    // Store FEN after the move for quick access
    this.fen = move ? move.after : null;
    this.moveNumber = moveNumber;
  }

  // Add a move (chess.js Move object) as a child
  addMove(move, moveNumber) {
    // Check if the move already exists as a variation (based on SAN)
    const existing = this.children.find((child) => child.move.san === move.san);
    if (existing) {
      return { node: existing, isNew: false };
    }
    const newNode = new PgnNode(move, this, moveNumber);
    // Ensure FEN is set correctly - if move.after is not available, calculate it
    if (!newNode.fen && move && move.after) {
      newNode.fen = move.after;
    } else if (!newNode.fen && this.fen) {
      // If we don't have move.after, calculate the FEN by applying the move
      try {
        const chess = new Chess(this.fen);
        const result = chess.move({
          from: move.from,
          to: move.to,
          promotion: move.promotion || 'q',
        });
        if (result) {
          newNode.fen = chess.fen();
        }
      } catch (e) {
        // Fallback to START_FEN if calculation fails
        newNode.fen =
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      }
    }
    this.children.push(newNode);
    return { node: newNode, isNew: true };
  }

  // Delete this node and its entire subtree
  deleteNode() {
    if (!this.parent) {
      // Cannot delete root node
      return false;
    }

    // Remove this node from parent's children array
    const index = this.parent.children.indexOf(this);
    if (index > -1) {
      this.parent.children.splice(index, 1);

      // If this was the main line (first child) and there are other variations,
      // promote the next variation to be the main line
      if (index === 0 && this.parent.children.length > 0) {
        // The next child automatically becomes the main line
        // No need to reorder since we just removed the first element
      }

      return true;
    }

    return false;
  }
}

// --- Serialization/Deserialization ---

// Helper to simplify a chess.js move object for storage (JSON compatible)
const simplifyMove = (move) => {
  if (!move) return null;
  // We only need enough info to replay the move (from, to, promotion)
  return {
    f: move.from,
    t: move.to,
    p: move.promotion,
  };
};

// Serialize PgnNode tree to plain object for storage
export const serializePgnTree = (node) => {
  if (!node) return { c: [] };
  const serializedNode = {
    // We don't store the move at the root node
    m: node.parent ? simplifyMove(node.move) : null,
    cm: node.comment || '', // Store comments
    c: node.children.map((child) => serializePgnTree(child)),
  };
  if (!serializedNode.m) delete serializedNode.m;
  if (!serializedNode.cm) delete serializedNode.cm;
  return serializedNode;
};

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// Deserialize plain object back to PgnNode tree by replaying moves
export const deserializePgnTree = (serializedTree, startFen = START_FEN) => {
  const chess = new Chess(startFen);
  const root = new PgnNode(null, null, 0);
  root.fen = startFen;

  const buildTree = (parentNode, childrenData) => {
    // Save the state before replaying moves from this node
    const fenBefore = chess.fen();

    childrenData.forEach((childData) => {
      // Reset to the parent state before playing the variation
      chess.load(fenBefore);

      // The move number we want to associate with the upcoming move
      const moveNumber = chess.moveNumber();

      const move = chess.move({
        from: childData.m.f,
        to: childData.m.t,
        promotion: childData.m.p,
      });

      if (move) {
        // Use the result object from addMove
        const { node: newNode } = parentNode.addMove(move, moveNumber);
        // Ensure the FEN is set correctly for this node
        newNode.fen = chess.fen();
        newNode.comment = childData.cm || ''; // Restore comments
        // Recursively build the rest of the tree
        buildTree(newNode, childData.c);
      }
    });

    // Reset chess instance to start after building the whole tree structure
    if (parentNode === root) {
      chess.load(startFen);
    }
  };

  if (serializedTree && serializedTree.c) {
    buildTree(root, serializedTree.c);
  }
  return root;
};
